import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { useRegion } from '../auth/RegionContext';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { JustifiedAction } from '../actions/JustifiedAction';
import { FRAUD_STATUS_COLOR, FRAUD_STATUS_LABEL } from '../fraud/fraudState';
import type { SuspensionRow as Row } from './suspensionsModel';
import {
  APPEAL_STATUS_LABEL,
  PHASE_COLOR,
  PHASE_LABEL,
  REASON_CODE_LABEL,
} from './suspensionState';

/**
 * One row of the suspended-users worklist (D-suspension-flow): the held account,
 * its structured reason code and free-text note, the period with the derived
 * elapsed / running indicator, the appeal state, and the lifecycle actions where
 * the role's capability permits.
 *
 * Resolution REUSES the existing actions, no first-class appeal-review action
 * (deferred). OVERTURN is the existing reinstate (suspension, fraud) or unban (ban,
 * super_admin only), so it inherits the fraud-foundation asymmetry for free; it
 * also clears the live hold fields (reason code, period, free-text reason) so an
 * active account carries no live reason, and resolves a pending appeal. DISMISS
 * resolves the appeal WITHOUT lifting (the original decision stands).
 */

/** The reinstate / overturn hygiene patch: back to active, every live hold field
 *  cleared, a pending appeal resolved. */
const liftData = (row: Row): Record<string, unknown> => ({
  fraudStatus: 'active',
  suspensionEndsAt: null,
  fraudReason: null,
  suspensionReasonCode: null,
  appealStatus: row.appealStatus === 'pending' ? 'resolved' : row.appealStatus,
});

const liftPrev = (row: Row): Record<string, unknown> => ({
  id: row.id,
  fraudStatus: row.fraudStatus,
  suspensionEndsAt: row.suspensionEndsAt,
  fraudReason: row.fraudReason,
  suspensionReasonCode: row.suspensionReasonCode,
  appealStatus: row.appealStatus,
});

export const SuspensionRow = ({ row }: { row: Row }) => {
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  const ctx = { role, accessibleRegions };
  const can = (capability: Parameters<typeof hasAccess>[1]['capability']) =>
    hasAccess(ctx, { capability });

  const isAppeal = row.appealStatus === 'pending';
  const isBanned = row.fraudStatus === 'banned';
  // Overturn is the existing reinstate (suspension) or unban (ban, super_admin
  // only). DISMISS keeps the hold, so it is only offered while an appeal is pending.
  const overturnCapability = isBanned ? 'users:unban' : 'users:reinstate';
  const overturnLabel = isAppeal ? 'Overturn' : 'Reinstate';

  return (
    <Card
      variant="outlined"
      data-testid={`suspension-user-${row.id}`}
      sx={{ mb: 2 }}
    >
      <CardContent>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.email} | {row.market.toUpperCase()}
            </Typography>
          </Box>
          <Chip
            size="small"
            variant="outlined"
            color={FRAUD_STATUS_COLOR[row.fraudStatus]}
            label={FRAUD_STATUS_LABEL[row.fraudStatus]}
          />
          {row.suspensionReasonCode && (
            <Chip
              size="small"
              variant="outlined"
              label={REASON_CODE_LABEL[row.suspensionReasonCode]}
              data-testid={`suspension-code-${row.id}`}
            />
          )}
          {row.phase && (
            <Chip
              size="small"
              variant="filled"
              color={PHASE_COLOR[row.phase]}
              label={PHASE_LABEL[row.phase]}
              data-testid={`suspension-phase-${row.id}`}
            />
          )}
          {row.appealStatus !== 'none' && (
            <Chip
              size="small"
              variant="outlined"
              color={isAppeal ? 'info' : 'default'}
              label={APPEAL_STATUS_LABEL[row.appealStatus]}
            />
          )}
          {row.fraudStatus === 'suspended' && row.suspensionEndsAt && (
            <Typography variant="caption" color="text.secondary">
              until {row.suspensionEndsAt.slice(0, 10)}
            </Typography>
          )}
        </Stack>

        {row.fraudReason && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ mt: 1 }}
          >
            Reason: {row.fraudReason}
          </Typography>
        )}
        {isAppeal && row.appealNote && (
          <Typography
            variant="body2"
            component="div"
            sx={{ mt: 1 }}
            data-testid={`suspension-appealnote-${row.id}`}
          >
            Appeal: {row.appealNote}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 2 }}
          useFlexGap
          flexWrap="wrap"
        >
          {can(overturnCapability) ? (
            <JustifiedAction
              resource="users"
              capability={overturnCapability}
              id={row.id}
              label={overturnLabel}
              color="primary"
              dialogTitle={
                isBanned ? 'Reverse this ban' : 'Lift the suspension'
              }
              dialogIntro="Return the account to active. This clears the live hold and resolves any appeal. Record why."
              buildData={() => liftData(row)}
              previousData={liftPrev(row)}
              successMessage={isBanned ? 'Ban reversed' : 'Suspension lifted'}
            />
          ) : (
            isAppeal &&
            isBanned && (
              <Typography variant="caption" color="text.secondary">
                Overturning a ban is a super_admin only reversal.
              </Typography>
            )
          )}
          {isAppeal && can('users:dismiss_appeal') && (
            <JustifiedAction
              resource="users"
              capability="users:dismiss_appeal"
              id={row.id}
              label="Dismiss appeal"
              color="warning"
              dialogTitle="Dismiss this appeal"
              dialogIntro="The original decision stands. The hold is unchanged. Record why."
              buildData={() => ({ appealStatus: 'resolved' })}
              previousData={{ id: row.id, appealStatus: row.appealStatus }}
              successMessage="Appeal dismissed"
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
